import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, Trash2, Edit2, ArrowLeft, Tag, X, Check, Newspaper, Calendar, Search, Filter, FileText, User } from 'lucide-react';
import { listNews, createNews, updateNews, deleteNews, adminVerify, DEFAULT_BASE_URL } from '../../services/api';
import WysiwygEditor from '../../components/WysiwygEditor';
import toast from 'react-hot-toast';

const emptyForm = { title: '', summary: '', content: '', author: '', category: '', imageUrl: '' };

const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024;

const getApiOrigin = () => {
  try {
    const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${getApiOrigin()}${url}`;
  return url;
};

const AdminNewsPage = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [filters, setFilters] = useState({
    title: '',
    author: '',
    category: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [categories, setCategories] = useState([
    'Regulatory Updates',
    'Events',
    'Infrastructure',
    'Environmental',
    'Legal Updates',
    'Industry News',
    'Policy Updates'
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
        // Categories are extracted in refresh() function via setTimeout
      } catch (e) {
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl('');
      return undefined;
    }

    const preview = URL.createObjectURL(imageFile);
    setImagePreviewUrl(preview);

    return () => URL.revokeObjectURL(preview);
  }, [imageFile]);

  function extractCategoriesFromNews() {
    if (!Array.isArray(items) || items.length === 0) return;
    const uniqueCats = [...new Set(items.map(item => item?.category).filter(Boolean))];
    if (uniqueCats.length > 0) {
      setCategories(prev => {
        const combined = [...new Set([...prev, ...uniqueCats])];
        return combined.sort();
      });
    }
  }

  async function refresh() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });
      
      const data = await listNews(params);
      
      // Handle response format
      let news = [];
      let paginationInfo = null;
      
      if (Array.isArray(data)) {
        // Old format - just array
        news = data.sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.createdAt || a.date || 0);
          const dateB = new Date(b.publishedAt || b.createdAt || b.date || 0);
          return dateB - dateA; // Descending order
        });
      } else if (data.news) {
        // New format - object with news and pagination
        news = data.news.sort((a, b) => {
          const dateA = new Date(a.publishedAt || a.createdAt || a.date || 0);
          const dateB = new Date(b.publishedAt || b.createdAt || b.date || 0);
          return dateB - dateA; // Descending order
        });
        paginationInfo = {
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          page: data.page || 1
        };
      } else {
        news = [];
      }
      
      setAllItems(news);
      setItems(news);
      
      // Update pagination if data has pagination info
      if (paginationInfo) {
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total,
          totalPages: paginationInfo.totalPages
        }));
      }
      
      // Extract categories after loading news
      setTimeout(() => {
        const uniqueCats = [...new Set(news.map(item => item.category).filter(Boolean))];
        setCategories(prev => {
          const combined = [...new Set([...prev, ...uniqueCats])];
          return combined.sort();
        });
      }, 100);
    } catch (e) {
      const errorMsg = e.message || 'Failed to load news';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Image size must be 1MB or less');
      event.target.value = '';
      return;
    }
    setImageFile(file);
    setForm(prev => ({ ...prev, imageUrl: '' }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.summary || !form.content || !form.category) {
      setError('Please fill in all required fields including category');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        title: form.title,
        summary: form.summary,
        content: form.content,
        author: form.author,
        category: form.category,
        image: form.imageUrl || undefined
      };

      const shouldUseUpload = imageInputMode === 'upload';
      let requestBody = payload;

      if (shouldUseUpload && imageFile) {
        const formData = new FormData();
        formData.append('__payload', JSON.stringify(payload));
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (typeof value === 'object' && !(value instanceof Date)) {
            formData.append(key, JSON.stringify(value));
            return;
          }
          formData.append(key, value);
        });
        formData.append('image', imageFile);
        requestBody = formData;
      } else if (imageInputMode === 'url') {
        payload.image = form.imageUrl;
      } else if (editingId && existingImageUrl) {
        payload.image = existingImageUrl;
      }

      if (editingId) {
        await updateNews(editingId, requestBody);
        setSuccess('News updated successfully!');
      } else {
        await createNews(requestBody);
        setSuccess('News created successfully!');
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      setImageFile(null);
      setExistingImageUrl('');
      setImageInputMode('url');
      setShowAddCategory(false);
      setNewCategory('');
      await refresh();
      toast.success(editingId ? ('News updated successfully!') : ('News created successfully!'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Save failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this news item?')) return;
    try {
      await deleteNews(id);
      setSuccess('News deleted successfully!');
      toast.success('News deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Delete failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }

  function startEdit(item) {
    const itemImage = item.imageUrl || item.image || '';
    setEditingId(item._id);
    setForm({
      title: item.title || '',
      summary: item.summary || '',
      content: item.content || '',
      author: item.author || '',
      category: item.category || '',
      imageUrl: itemImage
    });
    setShowForm(true);
    setImageFile(null);
    setExistingImageUrl(itemImage);
    setImageInputMode(itemImage ? 'url' : 'upload');
    setError('');
    setSuccess('');
    setShowAddCategory(false);
    setNewCategory('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(false);
    setImageFile(null);
    setExistingImageUrl('');
    setImageInputMode('url');
    setError('');
    setSuccess('');
    setShowAddCategory(false);
    setNewCategory('');
  }

  function handleNew() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setImageFile(null);
    setExistingImageUrl('');
    setImageInputMode('url');
    setError('');
    setSuccess('');
    setShowAddCategory(false);
    setNewCategory('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleAddCategory() {
    const trimmed = newCategory.trim();
    if (!trimmed) {
      setError('Category name cannot be empty');
      return;
    }
    if (categories.includes(trimmed)) {
      setError('Category already exists');
      return;
    }
    setCategories(prev => [...prev, trimmed].sort());
    setForm({ ...form, category: trimmed });
    setNewCategory('');
    setShowAddCategory(false);
    setError('');
    setSuccess('Category added successfully!');
    setTimeout(() => setSuccess(''), 2000);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'News'}</h1>
              <p className="text-xs text-gray-500">Create and manage news articles</p>
            </div>
          </div>
          {!showForm && (
            <button
              onClick={handleNew}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-2.5 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              {'Create New News'}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? 'Edit News' : 'Create New News'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingId ? 'Update the news article details below' : 'Fill in the form below to create a new news article'}
                </p>
              </div>
              <button
                onClick={resetForm}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Close form"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {'Title'} <span className="text-red-500">*</span>
                </label>
                <input 
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white" 
                  value={form.title} 
                  onChange={e => setForm({ ...form, title: e.target.value })} 
                  placeholder="Enter news title"
                  required 
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {'Author'} <span className="text-red-500">*</span>
                </label>
                <input 
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white" 
                  value={form.author} 
                  onChange={e => setForm({ ...form, author: e.target.value })} 
                  placeholder="Enter author name"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {'Category'} <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <div className="flex items-stretch gap-3">
                  <select
                    className="flex-1 border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white text-gray-900"
                    value={form.category}
                    onChange={e => {
                      setForm({ ...form, category: e.target.value });
                      setError('');
                    }}
                    required
                  >
                    <option value="">{'Select'} {'category'}</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCategory(!showAddCategory);
                      setNewCategory('');
                      setError('');
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                      showAddCategory 
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    } shadow-sm`}
                  >
                    <Tag className="h-4 w-4" />
                    {showAddCategory ? ('Cancel') : ('New Category')}
                  </button>
                </div>
                
                {showAddCategory && (
                  <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Enter new category name"
                        value={newCategory}
                        onChange={e => {
                          setNewCategory(e.target.value);
                          setError('');
                        }}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCategory();
                          }
                        }}
                        className="flex-1 border-2 border-primary-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm font-medium"
                      >
                        <Check className="h-4 w-4" />
                        {'Add'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Press Enter or click Add to create the new category
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {'Summary'} <span className="text-red-500">*</span>
              </label>
              <input 
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white" 
                value={form.summary} 
                onChange={e => setForm({ ...form, summary: e.target.value })} 
                placeholder="Brief summary of the news (shown in news list)"
                required
              />
              <p className="text-xs text-gray-500">This will be displayed in the news listing page</p>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                News Image
              </label>
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="radio"
                    name="news-image-mode"
                    value="url"
                    checked={imageInputMode === 'url'}
                    onChange={() => setImageInputMode('url')}
                  />
                  Use Image URL
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="radio"
                    name="news-image-mode"
                    value="upload"
                    checked={imageInputMode === 'upload'}
                    onChange={() => setImageInputMode('upload')}
                  />
                  Upload Image
                </label>
              </div>

              {imageInputMode === 'url' ? (
                <input
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                  value={form.imageUrl}
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder="https://example.com/news-image.jpg"
                />
              ) : (
                <div className="flex flex-col gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileChange}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                  />
                  <p className="text-xs text-gray-500">Max file size: 1MB. JPG/PNG/WebP recommended.</p>
                </div>
              )}

              {(imagePreviewUrl || form.imageUrl || existingImageUrl) && (
                <div className="mt-2">
                  <div className="w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                    <img
                      src={imagePreviewUrl || getImageSrc(form.imageUrl || existingImageUrl)}
                      alt="News preview"
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {'Content'} <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all bg-white">
                <WysiwygEditor
                  value={form.content} 
                  onChange={(content) => setForm({ ...form, content: content })}
                  placeholder="Enter full news content with rich text formatting..."
                  height="400px"
                />
              </div>
              <p className="text-xs text-gray-500">
                Use the toolbar to format text: bold, italic, colors, lists, headers, and more. This content will be displayed on the news detail page.
              </p>
            </div>
            
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
              <button 
                type="submit" 
                disabled={saving} 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg font-semibold"
              >
                <Save className="h-5 w-5" /> 
                {saving ? ('Loading...') : editingId ? 'Update' : 'Create'}
              </button>
              <button 
                type="button" 
                onClick={resetForm} 
                className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
              >
                {'Cancel'}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
              {Object.values(filters).some(f => f) && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f).length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({
                    title: '',
                    author: '',
                    category: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                {'Clear All'}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {/* Title Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Title'}
              </label>
              <div className="relative">
                <FileText className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.title ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search title..."
                  value={filters.title}
                  onChange={(e) => handleFilterChange('title', e.target.value)}
                />
                {filters.title && (
                  <button
                    onClick={() => handleFilterChange('title', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Author Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Author'}
              </label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.author ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search author..."
                  value={filters.author}
                  onChange={(e) => handleFilterChange('author', e.target.value)}
                />
                {filters.author && (
                  <button
                    onClick={() => handleFilterChange('author', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                {'Category'}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                From Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                To Date
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate}
              />
            </div>
          </div>

        </div>

        {/* List */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          </div>
          
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-gray-600">{'Loading...'}</p>
              </div>
            ) : items.length === 0 ? (
              // Check if filters are active or if it's truly empty
              (filters.title || filters.author || filters.category || filters.startDate || filters.endDate) ? (
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 mb-2 font-medium">No news articles found.</p>
                  <p className="text-sm text-gray-500 mb-6">Try adjusting your filters</p>
                  <button 
                    onClick={() => {
                      setFilters({
                        title: '',
                        author: '',
                        category: '',
                        startDate: '',
                        endDate: ''
                      });
                      setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all shadow-lg font-semibold"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                    <Newspaper className="h-8 w-8 text-primary-600" />
                  </div>
                  <p className="text-gray-600 mb-2 font-medium">No news articles yet.</p>
                  <p className="text-sm text-gray-500 mb-6">Get started by creating your first news article</p>
                  <button 
                    onClick={handleNew}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg font-semibold"
                  >
                    <Plus className="h-5 w-5" /> Create Your First News Article
                  </button>
                </div>
              )
            ) : (
              items.map(item => (
                <div key={item._id} className="p-6 flex items-start justify-between gap-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-3 flex-wrap">
                      <h4 className="font-bold text-gray-900 text-lg flex-1 min-w-[200px]">{item.title}</h4>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700 whitespace-nowrap border border-primary-200 shadow-sm">
                        <Tag className="h-3 w-3" />
                        {item.category || 'Uncategorized'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{item.summary}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                        <span className="font-semibold text-gray-700">Author:</span> 
                        <span className="text-gray-600">{item.author || 'N/A'}</span>
                      </span>
                      {item.publishedAt && (
                        <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
                          <Calendar className="h-3.5 w-3.5 text-gray-500" />
                          <span className="text-gray-600">
                            {new Date(item.publishedAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button 
                      onClick={() => startEdit(item)} 
                      className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Edit article"
                    >
                      <Edit2 className="h-5 w-5 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleDelete(item._id)} 
                      className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Delete article"
                    >
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {'Page'} {pagination.page} {'of'} {pagination.totalPages} ({pagination.total || items.length} {'total'})
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Previous'}
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminNewsPage;


